import * as React from "react";

const generateSrcSet = (imageUrl, formatType = 'original') => {
    const sizes = [320, 480, 640, 960, 1280, 1440, 1600, 1920];
    
    // Clean existing parameters and apply format
    let cleanUrl = imageUrl?.replace(/&(width|height|format)=\d+|&format=\w+/g, '');
    
    // Add format parameter for WebP/AVIF
    if (formatType === 'webp') {
        cleanUrl += '&format=webp';
    } else if (formatType === 'avif') {
        cleanUrl += '&format=avif';
    }
    
    return sizes
        .map(size => `${cleanUrl}&width=${size} ${size}w`)
        .join(', ');
};

interface ImageComponentProps {
    imageUrl: string;
    alt?: string;
    isLazy?: boolean;
    classes?: string;
    style?: React.CSSProperties;
    useSrcSet?: boolean;
    height?: number | string;
    width?: number | string;
    priority?: boolean;
    sizes?: string;
    aspectRatio?: string;
    placeholderColor?: string;
}

const ImageComponent: React.FC<ImageComponentProps> = ({
    imageUrl,
    alt = "",
    isLazy = true,
    classes = '',
    style = {},
    useSrcSet = true,
    height = 'auto',
    width = 'auto',
    priority = false,
    sizes,
    aspectRatio,
    placeholderColor = '#f0f0f0'
}) => {
    const imgRef = React.useRef<HTMLImageElement>(null);

    // Mark as LCP candidate if priority is set
    React.useEffect(() => {
        if (priority && imgRef.current) {
            imgRef.current.dataset.lcpCandidate = 'true';
        }
    }, [priority]);
    // Determine loading strategy based on priority and lazy flags
    const loading = priority ? 'eager' : (isLazy ? 'lazy' : 'eager');
    const fetchPriority = priority ? 'high' : 'auto';
    
    // Smart sizes based on common layouts if not provided
    const defaultSizes = sizes || 
        "(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 75vw";
    
    // Generate different format srcsets for modern browsers
    const avifSrcSet = generateSrcSet(imageUrl, 'avif');
    const webpSrcSet = generateSrcSet(imageUrl, 'webp');
    const jpegSrcSet = generateSrcSet(imageUrl, 'original');
    
    // Base image URL for fallback (optimized size)
    const fallbackSrc = imageUrl?.includes('&width=') 
        ? imageUrl 
        : `${imageUrl}&width=800&format=jpg`;
    
    // Container style with aspect ratio if provided
    const containerStyle = {
        ...style,
        ...(aspectRatio && {
            aspectRatio,
            overflow: 'hidden'
        })
    };
    
    // Placeholder while loading for better CLS
    const placeholderStyle = {
        backgroundColor: placeholderColor,
        minHeight: height !== 'auto' ? height : '200px',
        display: 'block'
    };

    if (!imageUrl) {
        return (
            <div 
                className={`image-placeholder ${classes}`}
                style={{...containerStyle, ...placeholderStyle}}
                aria-label={alt || "Placeholder image"}
            />
        );
    }

    return (
        <div style={containerStyle}>
            {useSrcSet ? (
                <picture>
                    {/* AVIF format for modern browsers */}
                    <source 
                        srcSet={avifSrcSet}
                        type="image/avif" 
                        sizes={defaultSizes}
                    />
                    {/* WebP format fallback */}
                    <source 
                        srcSet={webpSrcSet}
                        type="image/webp" 
                        sizes={defaultSizes}
                    />
                    {/* JPEG fallback */}
                    <source 
                        srcSet={jpegSrcSet}
                        type="image/jpeg" 
                        sizes={defaultSizes}
                    />
                    <img
                        ref={imgRef}
                        src={fallbackSrc}
                        alt={alt}
                        loading={loading}
                        className={classes}
                        width={width !== 'auto' ? width : undefined}
                        height={height !== 'auto' ? height : undefined}
                        {...(fetchPriority === 'high' && { fetchpriority: 'high' as any })}
                        decoding="async"
                        onError={(e) => {
                            // Fallback to original image if optimized versions fail
                            const target = e.target as HTMLImageElement;
                            if (target.src !== imageUrl) {
                                target.src = imageUrl;
                            }
                        }}
                    />
                </picture>
            ) : (
                <img
                    ref={imgRef}
                    src={fallbackSrc}
                    alt={alt}
                    loading={loading}
                    className={classes}
                    style={style}
                    width={width !== 'auto' ? width : undefined}
                    height={height !== 'auto' ? height : undefined}
                    {...(fetchPriority === 'high' && { fetchpriority: 'high' as any })}
                    decoding="async"
                    onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        if (target.src !== imageUrl) {
                            target.src = imageUrl;
                        }
                    }}
                />
            )}
        </div>
    );
};

export default ImageComponent;